import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const AdvertiserWallet = () => {
  const navigate = useNavigate();
  const [walletData, setWalletData] = useState({
    balance: 0,
    transactions: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch wallet data (balance and transactions) from single API
  const fetchWalletData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/get/wallet/detail`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setWalletData({
        balance: response.data.balance || 0,
        transactions: response.data.transactions || [],
      });
    } catch (error) {
      console.error("Error fetching wallet data:", error);
      setError("Failed to load wallet data");
    }
  };

  useEffect(() => {
    const loadWalletData = async () => {
      setLoading(true);
      setError(null);
      try {
        await fetchWalletData();
      } finally {
        setLoading(false);
      }
    };
    loadWalletData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "text-green-700 bg-green-50 border-green-200";
      case "Pending":
        return "text-amber-700 bg-amber-50 border-amber-200";
      case "Failed":
        return "text-red-700 bg-red-50 border-red-200";
      default:
        return "text-gray-700 bg-gray-50 border-gray-200";
    }
  };

  // Pagination functions
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Sort transactions by date descending (latest first)
  const sortedTransactions = [...walletData.transactions].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Calculate pagination values
  const totalPages = Math.ceil(sortedTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTransactions = sortedTransactions.slice(startIndex, endIndex);

  return (
    <div className="max-w-5xl mx-auto space-y-4 sm:space-y-6 pb-20 sm:pb-8 p-2 sm:p-4 md:p-6 mt-2">
      {/* Header & Balance Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-slate-900 tracking-tight uppercase">Financial Wallet</h1>
          <p className="text-xs sm:text-sm font-bold text-slate-500 tracking-wide">Monitor your advertising credits and expenses</p>
        </div>

        <div className="bg-gradient-to-br from-indigo-700 via-blue-600 to-indigo-900 p-6 rounded-[2rem] text-white shadow-xl shadow-blue-200/50 flex items-center justify-between sm:w-80 relative overflow-hidden group border border-blue-400/30">
          <div className="relative z-10">
            <p className="text-[10px] sm:text-xs font-black text-blue-100 uppercase tracking-widest mb-1 opacity-90 drop-shadow-sm">Available Liquidity</p>
            <p className="text-3xl sm:text-4xl font-[1000] tracking-tighter drop-shadow-md">₹{walletData.balance.toFixed(2)}</p>
          </div>
          <div className="relative z-10 p-3 sm:p-4 bg-white/20 rounded-2xl backdrop-blur-md border-2 border-white/30 shadow-inner transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
            <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 10h18M7 15h1m4 0h1m-7 4h12l1-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v13l1 1zm3-7v4m4-4v4m4-4v4" />
            </svg>
          </div>
          {/* Abstract background shapes */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-700 blur-xl"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-8 -mb-8 blur-2xl"></div>
        </div>
      </div>

      {/* Quick Action Buttons - Strategic for Mobile */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => navigate('/advertiser/addmoney')}
          className="group relative flex flex-col items-center justify-center p-4 sm:p-5 bg-white rounded-[1.5rem] border-2 border-slate-100 shadow-sm hover:shadow-lg hover:border-emerald-200 transition-all duration-300 active:scale-95"
        >
          <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center mb-2 border border-emerald-100 group-hover:scale-110 group-hover:bg-emerald-500 group-hover:text-white transition-all duration-300">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <span className="text-sm font-black text-slate-800 tracking-tight">Add Money</span>
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Instant Top Up</span>
        </button>

        <button
          onClick={() => navigate('/advertiser/advertiserpaymenthistory')}
          className="group relative flex flex-col items-center justify-center p-4 sm:p-5 bg-white rounded-[1.5rem] border-2 border-slate-100 shadow-sm hover:shadow-lg hover:border-blue-200 transition-all duration-300 active:scale-95"
        >
          <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-2 border border-blue-100 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </div>
          <span className="text-sm font-black text-slate-800 tracking-tight">Payments</span>
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Full Ledger</span>
        </button>
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <h2 className="text-base sm:text-lg font-black text-slate-800 flex items-center gap-2 uppercase tracking-tight">
            <span className="w-2 h-6 bg-indigo-600 rounded-full"></span>
            Recent Transactions
          </h2>
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-200 px-2 py-1 rounded-md">{sortedTransactions.length} Total</span>
        </div>

        {loading ? (
          <div className="p-20 flex flex-col items-center justify-center text-gray-400">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-sm font-bold">Securely fetching transactions...</p>
          </div>
        ) : sortedTransactions.length === 0 ? (
          <div className="p-16 flex flex-col items-center justify-center text-center">
            <div className="p-6 bg-gray-50 rounded-full mb-4">
              <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-gray-900">Empty Transaction List</h3>
            <p className="text-sm text-gray-500 mt-1 max-w-xs">You haven't made any transactions yet. Your wallet activity will appear here.</p>
          </div>
        ) : (
          <div className="overflow-hidden">
            {/* Desktop Table Header */}
            <div className="hidden md:grid grid-cols-6 gap-4 px-6 py-4 bg-slate-100 text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-200">
              <div className="col-span-1">Date & Time</div>
              <div>Type</div>
              <div>Amount</div>
              <div>Status</div>
              <div className="col-span-2">Description / ID</div>
            </div>

            {/* List for Mobile, Table-like rows for Desktop */}
            <div className="divide-y divide-slate-100">
              {currentTransactions.map((transaction, idx) => (
                <div key={idx} className="block md:grid md:grid-cols-6 md:gap-4 px-6 py-5 md:py-4 transition-colors hover:bg-slate-50">
                  {/* Mobile Row 1: Amount & Status */}
                  <div className="flex md:hidden items-center justify-between mb-3">
                    <span className={`text-lg font-[1000] tracking-tighter ${transaction.type.toLowerCase() === "debit" ? "text-rose-600" : "text-emerald-600"}`}>
                      {transaction.type.toLowerCase() === "debit" ? "-" : "+"}₹{Number(transaction.amount).toFixed(2)}
                    </span>
                    <span className={`px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest border ${getStatusColor(transaction.status)}`}>
                      {transaction.status}
                    </span>
                  </div>

                  {/* Date column */}
                  <div className="md:col-span-1 mb-2 md:mb-0 flex flex-col justify-center">
                    <p className="text-xs md:text-sm font-black text-slate-800 uppercase">
                      {new Date(transaction.date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" })}
                    </p>
                    <p className="text-[10px] text-slate-500 font-bold font-mono">
                      {new Date(transaction.date).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}
                    </p>
                  </div>

                  {/* Desktop Only columns */}
                  <div className="hidden md:flex items-center">
                    <span className="text-xs font-black text-slate-600 uppercase tracking-wider bg-slate-100 px-2 py-1 rounded border border-slate-200">{transaction.type}</span>
                  </div>

                  <div className="hidden md:flex items-center">
                    <span className={`text-[15px] font-[1000] tracking-tighter ${transaction.type.toLowerCase() === "debit" ? "text-rose-600" : "text-emerald-600"}`}>
                      {transaction.type.toLowerCase() === "debit" ? "-" : "+"}₹{Number(transaction.amount).toFixed(2)}
                    </span>
                  </div>

                  <div className="hidden md:flex items-center">
                    <span className={`px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest border shadow-sm ${getStatusColor(transaction.status)}`}>
                      {transaction.status}
                    </span>
                  </div>

                  {/* Description / ID column */}
                  <div className="md:col-span-2 flex flex-col justify-center">
                    <p className="text-xs md:text-sm text-slate-700 font-bold truncate pr-2">{transaction.description}</p>
                    <p className="text-[10px] text-slate-400 font-mono font-bold mt-1 max-w-[200px] truncate">ID: {transaction.refundId || transaction.paymentId || "N/A"}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-6 py-5 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-white border-2 border-slate-100 rounded-lg text-xs font-black text-slate-700 uppercase tracking-widest hover:border-indigo-200 hover:text-indigo-600 active:scale-95 disabled:opacity-50 disabled:active:scale-100 shadow-sm transition-all"
              >
                Prev
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-white border-2 border-slate-100 rounded-lg text-xs font-black text-slate-700 uppercase tracking-widest hover:border-indigo-200 hover:text-indigo-600 active:scale-95 disabled:opacity-50 disabled:active:scale-100 shadow-sm transition-all"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvertiserWallet;
